import React, { useState, useEffect, useRef, useContext } from "react"

import "./Question.scss"

import { QuizActions, useQuiz } from "@Quiz/context"
import QuestionNumber from "./QuestionNumber"
import QuestionQuestion from "./QuestionQuestion"
import QuestionAnswers, { usePreloadAnswerImages } from "./QuestionAnswers"
import { CountdownContext } from "./Countdown"
import QuizEmbedInfoContext from "@Quiz/QuizEmbedInfoContext"

const SHOW_QUESTION_NUMBER_FOR_MILLIS = 1500
const SHOW_QUESTION_NUMBER_FADE_OUT_FOR_MILLIS = 500 // sync with CSS ani!

type WhatToShowOfQuestion = "initiallyShowQuestion" | "showQuestionAgain" | "showAnswers"

interface IState {
  questionNumber: { show: boolean, fadeOut: boolean}
  whatToShowOfQuestions: WhatToShowOfQuestion
  answersFirstShownAt?: Date // important for countdown
  answeresAlreadyShowed: boolean
  countdownPerc: number
}

const RawQuestion: React.FC<{quiz: Quiz.Api.Quiz, showQuestionNumber: boolean, actions: QuizActions, question: Quiz.Api.Question}> = ({ quiz, question, actions, showQuestionNumber}) => {
  const questionRef = useRef<Quiz.Api.Question>(question)
  questionRef.current = question
  const actionsRef = useRef(actions)
  actionsRef.current = actions

  usePreloadAnswerImages(question.config)

  const [ __state, setState ] = useState<IState>({
    questionNumber: { show: showQuestionNumber, fadeOut: false },
    whatToShowOfQuestions: question.directlyShowAnswers
      ? "showAnswers"
      : (question.eval ? "showAnswers" : "initiallyShowQuestion"),
    answeresAlreadyShowed: false,
    answersFirstShownAt: ((question.countdown && question.config) || question.directlyShowAnswers) ? new Date() : undefined,
    countdownPerc: 100
  })
  const stateRef = useRef(__state)
  stateRef.current = __state

  useEffect(() => { // ------------------ fade out question number
    if (!stateRef.current.questionNumber.show) { return }
    let timeout = window.setTimeout(() => {
      setState({
        ...stateRef.current,
        questionNumber: { show: true, fadeOut: true }
      })
      timeout = window.setTimeout(() => {
        setState({
          ...stateRef.current,
          questionNumber: { show: false, fadeOut: true }
        })
      }, SHOW_QUESTION_NUMBER_FADE_OUT_FOR_MILLIS)
    }, SHOW_QUESTION_NUMBER_FOR_MILLIS)
    return () => { // cleanup
      window.clearTimeout(timeout)
    }
  }, [])

  useEffect(() => { // ------------------ countdown
    let interval: number

    if (questionRef.current.countdown) {
      interval = window.setInterval(() => {
        if (questionRef.current.countdown && stateRef.current.answersFirstShownAt && !questionRef.current.eval?.result) {
          const millisLeft = (stateRef.current.answersFirstShownAt.getTime() + questionRef.current.countdown?.millis_left) - new Date().getTime()
          let newPerc: number
          if (millisLeft < 0) {
            newPerc = 0
          } else {
            newPerc = Math.round((millisLeft / questionRef.current.countdown.millis_total) * 100)
          }
          if (newPerc <= 0) {
            window.clearInterval(interval)
            actionsRef.current.answerQuestion(undefined, true)
          }
          setState({
            ...stateRef.current,
            countdownPerc: newPerc
          })
        }
      }, 100)
    }

    return () => { // cleanup
      window.clearInterval(interval)
    }
  }, [__state.answersFirstShownAt, questionRef.current.countdown?.millis_left])

  const showAnswers = () => {
    if (!question?.config) {
      // A countdown question's config (the actual answer options) is deliberately withheld
      // by the server until the countdown has started - this prevents cheating by reading
      // the answers out of the network response's JSON before actually answering.
      // startQuestionCountdown() starts the countdown server-side, which makes the server
      // resend the question, this time including `config`.
      actions.startQuestionCountdown()
    }
    setState({
      ...stateRef.current,
      whatToShowOfQuestions: "showAnswers",
      answersFirstShownAt: stateRef.current.answersFirstShownAt || new Date() // when first time: set timestamp!
    })
  }

  const showQuestionAgain = () => {
    setState({
      ...stateRef.current,
      whatToShowOfQuestions: "showQuestionAgain",
      answeresAlreadyShowed: true
    })
  }

  const showQuestion = __state.whatToShowOfQuestions === "initiallyShowQuestion" || __state.whatToShowOfQuestions === "showQuestionAgain"

  return (
    <CountdownContext.Provider value={{hasCountdown: !!question?.countdown, perc: __state.countdownPerc, started: !!question?.countdown && !!__state.answersFirstShownAt}}>
      <div className="neo__quizQuestion" data-testid="quiz-question">
        {__state.questionNumber.show
          ? <QuestionNumber meta={quiz.meta} fadeOut={__state.questionNumber.fadeOut}/>
          : (showQuestion
            ? <QuestionQuestion showAgain={__state.whatToShowOfQuestions === "showQuestionAgain"} showAnswers={showAnswers} />
            : <QuestionAnswers
                showedForFirstTime={!__state.answeresAlreadyShowed}
                showQuestionAgain={showQuestionAgain} />)}
      </div>
    </CountdownContext.Provider>
  )
}

const Question: React.FC = () => {
  const { quiz, actions } = useQuiz()
  const { omitRoundNumbers } = useContext(QuizEmbedInfoContext)
  if (!quiz?.question) { return null }

  return <RawQuestion quiz={quiz} actions={actions} question={quiz.question} showQuestionNumber={quiz.meta.num_questions <= 10 && !omitRoundNumbers}/>
}

export default Question
